'use client';

import * as React from 'react';
import { getCommunities } from '@/api/communities';
import { Skeleton } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { CaretUpDown as CaretUpDownIcon } from '@phosphor-icons/react/dist/ssr/CaretUpDown';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import type { Community } from '@/types/v2/community';
import { paths } from '@/paths';
import { useDialog } from '@/hooks/v2/use-dialog';
import { usePopover } from '@/hooks/v2/use-popover';
import { useUser } from '@/hooks/v2/use-user';
import { CreateOrEditCommunityModal } from '@/components/communities/create-or-edit-community-modal';

import { CommunitiesPopover } from './communities-popover';

export function CommunitiesSwitch(): React.JSX.Element {
  const { community, setCommunity } = useUser();
  const navigate = useNavigate();
  const dialog = useDialog();

  const { data, isLoading } = useQuery({
    queryKey: ['communities-with-roles'],
    queryFn: () => getCommunities({ withRoles: true }),
    retry: false,
  });

  const popover = usePopover<HTMLDivElement>();

  const communities = data?.communities;

  const handleSetCommunity = React.useCallback(
    (community: Community) => {
      setCommunity(community, true);
      popover.handleClose();
      navigate(paths.home);
    },
    [setCommunity, navigate]
  );

  if (isLoading) {
    return (
      <Stack
        direction="row"
        onClick={popover.handleOpen}
        ref={popover.anchorRef}
        spacing={2}
        sx={{
          alignItems: 'center',
          border: '1px solid var(--Workspaces-border-color)',
          borderRadius: '12px',
          cursor: 'pointer',
          px: 2,
          py: 1,
          boxShadow: (theme) => theme.shadows[1],
          background: (theme) => theme.palette.background.paper,
        }}
      >
        <Avatar sx={{ bgcolor: 'grey.300' }} variant="square" />
        <Box sx={{ flex: '1 1 auto' }}>
          <Typography color="var(--Workspaces-title-color)" variant="caption">
            Community
          </Typography>
          <Skeleton sx={{ fontSize: '1rem' }} variant="text" />
        </Box>
        <CaretUpDownIcon color="var(--Workspaces-expand-color)" fontSize="var(--icon-fontSize-sm)" />
      </Stack>
    );
  }

  return (
    <React.Fragment>
      <Stack
        direction="row"
        onClick={popover.handleOpen}
        ref={popover.anchorRef}
        spacing={2}
        sx={{
          alignItems: 'center',
          border: '1px solid var(--Workspaces-border-color)',
          borderRadius: '12px',
          cursor: 'pointer',
          px: 2,
          py: 1,
          boxShadow: (theme) => theme.shadows[1],
          background: (theme) => theme.palette.background.paper,
        }}
      >
        <Avatar src={community?.logo_url} variant="square">
          {community?.name
            .split(' ')
            .map((name: string) => name.charAt(0))
            .join('')
            .toUpperCase()}
        </Avatar>
        <Box sx={{ flex: '1 1 auto', overflow: 'hidden' }}>
          <Typography color="var(--Workspaces-title-color)" variant="caption">
            Community
          </Typography>
          <Typography
            color="var(--Workspaces-name-color)"
            sx={{
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
            variant="subtitle2"
          >
            {community?.name}
          </Typography>
        </Box>
        <CaretUpDownIcon color="var(--Workspaces-expand-color)" fontSize="var(--icon-fontSize-sm)" />
      </Stack>
      <CommunitiesPopover
        anchorEl={popover.anchorRef.current}
        communities={communities || []}
        community={community}
        isLoading={isLoading}
        onAddCommunity={() => { dialog.handleOpen(); }}
        onChange={handleSetCommunity}
        onClose={popover.handleClose}
        open={popover.open}
      />
      <CreateOrEditCommunityModal
        mode="create"
        onClose={dialog.handleClose}
        onSuccess={handleSetCommunity}
        open={dialog.open}
      />
    </React.Fragment>
  );
}
